import i18n from "@/i18n";

/** BCP 47 tag used for dates and numbers. */
export function currentLocale(): string {
  if (i18n.resolvedLanguage) return i18n.resolvedLanguage;
  return "en-US";
}

export function formatDate(iso: string | Date | undefined, locale = currentLocale()): string {
  if (!iso) return "";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

export function truncate(text: string, max = 140): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

export function initials(name: string | undefined, fallback = "?"): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Money for display. Austrian and German both render EUR as "1.234,56 €". */
export function formatMoney(
  value: number | undefined | null,
  currency = "EUR",
  locale = currentLocale(),
): string {
  const amount = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
}

/** A VAT rate as printed on a receipt: "20 %", "4,9 %" (de) or "4.9 %" (en). */
export function formatRate(rate: number, locale = currentLocale()): string {
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(rate)} %`;
}

export function formatPercent(value: number, locale = currentLocale()): string {
  return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(
    value,
  );
}

/** An amount in an input field: two decimals, no grouping, "5,20" (de) or "5.20" (en). */
export function formatAmountInput(value: number, locale = currentLocale()): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(value);
}

/**
 * Reads an amount the user typed. Accepts both the Austrian "1.234,56" and the plain "1234.56",
 * so switching the keyboard layout never costs data.
 */
export function parseDecimal(input: string | number | undefined | null): number | undefined {
  if (typeof input === "number") return Number.isFinite(input) ? input : undefined;
  if (!input) return undefined;
  const cleaned = String(input)
    .trim()
    .replace(/[^\d,.-]/g, "");
  if (!/\d/.test(cleaned)) return undefined;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const normalized =
    lastComma > lastDot ? cleaned.replace(/\./g, "").replace(",", ".") : cleaned.replace(/,/g, "");
  const value = Number(normalized);
  return Number.isFinite(value) ? value : undefined;
}
